import React, {ReactNode, memo, useEffect, useState, useMemo, useCallback} from 'react';

import {Colors, Tooltip} from '@optimaxdev/design-system';
import {usePrevious} from '@optimaxdev/utils';

import s from './ActionTooltip.scss';

const timeout = 3;
const delay = 0.5;

type PropsType = {
    qty: number | string;
    children: ReactNode;
};

const doveGray = '#6d6d6d';

const PARAMS_WITH_EVENT = {color: 'blue'} as const;
const PARAMS_WITHOUT_EVENT = {
    arrowBorderColor: doveGray,
    arrowColor: doveGray,
    textColor: Colors.white,
    tooltipBorderColor: doveGray,
    tooltipColor: doveGray,
} as const;

/**
 * Action Tooltip component
 */
const ActionTooltip = memo<PropsType>(({children, qty}) => {
    const [event, setEvent] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const prevQty = usePrevious(qty);

    useEffect(() => {
        let shiftDelay: NodeJS.Timeout;

        if (prevQty && qty !== prevQty) {
            const isAdded = prevQty < qty;
            if (showTooltip) {
                setShowTooltip(false);
                shiftDelay = setTimeout(() => {
                    setShowTooltip(true);
                    setEvent(isAdded);
                }, delay * 1000);
            } else {
                setShowTooltip(true);
                setEvent(isAdded);
            }
        }
        return () => {
            if (shiftDelay) {
                window.clearTimeout(shiftDelay);
            }
        };
    }, [prevQty, qty, showTooltip]);

    const text = event ? 'Added' : 'Removed';

    const tooltipParams = useMemo(
        () => (event ? PARAMS_WITH_EVENT : PARAMS_WITHOUT_EVENT),
        [event],
    );

    const onTooltipChange = useCallback((visible: boolean) => {
        if (!visible) {
            setShowTooltip(visible);
        }
    }, []);

    return (
        <Tooltip
            {...tooltipParams}
            content={<div className={s.tooltipContent}>{text}</div>}
            elevation={1}
            isOpen={showTooltip}
            offset={-6}
            onChange={onTooltipChange}
            position="right-end"
            renderAs="div"
            size="small"
            timeToAutoClose={timeout * 1000}
        >
            {children}
        </Tooltip>
    );
});

ActionTooltip.displayName = 'ActionTooltip';

export default ActionTooltip;
